import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { CANVAS_JSON_PROPS } from '../../../config';
import { isSlideLoaded } from '../../../core/hooks/useLoadJSON';
import { useSnack } from '../../../../../util/useSnack';
import { useEditorData } from './EditorProvider';
import { useTooling } from './EditorToolingProvider';

export type SaveStatus = 'saved' | 'unsaved' | 'saving' | 'error';

interface RecoveryCopy {
  // Fingerprint of the saved slide the changes were made on.
  base: string;
  changedAt: number;
  json: string;
}

type Recovery =
  | { state: 'checking' }
  | { state: 'prompt'; copy: RecoveryCopy; outdated: boolean }
  | { state: 'done'; restored: boolean };

interface SaveState {
  status: SaveStatus;
  save: () => Promise<boolean>;
  discard: () => void;
  leaveWithoutSaving: () => void;
  markLoaded: (canvas: fabric.Canvas) => void;
  markChanged: (canvas: fabric.Canvas) => void;
  recovery: Recovery;
  resolveRecovery: (restore: boolean) => void;
  // What the canvas should load: the saved slide or restored changes.
  initialProjectData: any;
}

const COMPARE_DELAY = 300;
const RECOVERY_KEY_PREFIX = 'canvasEditor.unsaved.';

const recoveryKey = (slideId: string) => `${RECOVERY_KEY_PREFIX}${slideId}`;

const readRecovery = (slideId: string): RecoveryCopy | null => {
  try {
    const stored = localStorage.getItem(recoveryKey(slideId));
    return stored ? JSON.parse(stored) : null;
  } catch {
    return null;
  }
};

const writeRecovery = (slideId: string, copy: RecoveryCopy) => {
  try {
    localStorage.setItem(recoveryKey(slideId), JSON.stringify(copy));
  } catch {
    // Storage full or blocked: unsaved changes are only kept in the page.
  }
};

const clearRecovery = (slideId: string) => {
  try {
    localStorage.removeItem(recoveryKey(slideId));
  } catch {
    // Nothing to clean up.
  }
};

// Identifies a saved version of the slide by its content, so a recovery copy
// is only offered as-is on top of the version it was made from.
const fingerprint = (projectData: any) => {
  const text = JSON.stringify([
    projectData?.objects ?? [],
    projectData?.background ?? null,
  ]);
  let hash = 5381;
  for (let index = 0; index < text.length; index++) {
    hash = ((hash << 5) + hash + text.charCodeAt(index)) | 0;
  }
  return `${text.length}:${hash}`;
};

const Context = createContext<SaveState | null>(null);

function SaveProvider({ children }: PropsWithChildren) {
  const { canvas } = useEditorData();
  const {
    slidesProps: { projectData, updateSlide, paramId },
  } = useTooling();
  const { showErrSnack } = useSnack();
  const slideId = paramId || '';

  const [status, setStatus] = useState<SaveStatus>('saved');
  const [recovery, setRecovery] = useState<Recovery>({ state: 'checking' });
  const [initialProjectData, setInitialProjectData] = useState<any>(null);
  // Serialized canvas as last saved; null when unknown (restored changes).
  const lastSavedJson = useRef<string | null>(null);
  const savedBase = useRef('');
  const compareTimer = useRef<ReturnType<typeof setTimeout>>();
  const allowUnload = useRef(false);
  const updateSlideRef = useRef(updateSlide);
  updateSlideRef.current = updateSlide;

  // Decide what to load before the canvas loads anything.
  useEffect(() => {
    if (recovery.state !== 'checking' || !projectData?.objects || !slideId) {
      return;
    }
    savedBase.current = fingerprint(projectData);
    const copy = readRecovery(slideId);
    if (copy) {
      setRecovery({
        state: 'prompt',
        copy,
        outdated: copy.base !== savedBase.current,
      });
    } else {
      setRecovery({ state: 'done', restored: false });
      setInitialProjectData(projectData);
    }
  }, [projectData, slideId, recovery.state]);

  const resolveRecovery = useCallback(
    (restore: boolean) => {
      if (recovery.state !== 'prompt') return;
      if (restore) {
        setInitialProjectData(JSON.parse(recovery.copy.json));
      } else {
        clearRecovery(slideId);
        setInitialProjectData(projectData);
      }
      setRecovery({ state: 'done', restored: restore });
    },
    [recovery, projectData, slideId],
  );

  const serialize = (target: fabric.Canvas) =>
    JSON.stringify(target.toJSON(CANVAS_JSON_PROPS));

  const compare = useCallback(
    (target: fabric.Canvas) => {
      if (!target.getContext()) return;
      const json = serialize(target);
      const changed = json !== lastSavedJson.current;
      setStatus((current) => {
        if (current === 'saving') return current;
        return changed ? 'unsaved' : 'saved';
      });
      // A canvas that failed to load completely must never be restored.
      if (changed && isSlideLoaded(target)) {
        writeRecovery(slideId, {
          base: savedBase.current,
          changedAt: Date.now(),
          json,
        });
      } else if (!changed) {
        clearRecovery(slideId);
      }
    },
    [slideId],
  );

  const markLoaded = useCallback(
    (target: fabric.Canvas) => {
      if (recovery.state === 'done' && recovery.restored) {
        lastSavedJson.current = null;
        compare(target);
      } else {
        lastSavedJson.current = serialize(target);
        setStatus('saved');
      }
    },
    [recovery, compare],
  );

  const markChanged = useCallback(
    (target: fabric.Canvas) => {
      clearTimeout(compareTimer.current);
      compareTimer.current = setTimeout(() => compare(target), COMPARE_DELAY);
    },
    [compare],
  );

  const save = useCallback(async () => {
    if (!canvas || !canvas.getContext()) return false;
    // Saving a canvas the slide was not completely loaded into would delete
    // the items that failed to load.
    if (!isSlideLoaded(canvas)) {
      showErrSnack(
        'Some items on this slide failed to load. Reload the page before editing; this slide cannot be saved.',
      );
      return false;
    }
    clearTimeout(compareTimer.current);
    const data = canvas.toJSON(CANVAS_JSON_PROPS);
    const json = JSON.stringify(data);
    if (json === lastSavedJson.current) {
      setStatus('saved');
      clearRecovery(slideId);
      return true;
    }
    setStatus('saving');
    return new Promise<boolean>((resolve) => {
      updateSlideRef.current(
        { id: slideId, projectData: data },
        {
          onSuccess: () => {
            lastSavedJson.current = json;
            savedBase.current = fingerprint(data);
            clearRecovery(slideId);
            setStatus('saved');
            // Edits made while the request was in flight are still unsaved.
            compare(canvas);
            resolve(true);
          },
          onError: (error: { msg?: string }) => {
            setStatus('error');
            showErrSnack(error?.msg || 'The slide could not be saved.');
            resolve(false);
          },
        },
      );
    });
  }, [canvas, slideId, compare, showErrSnack]);

  const leaveWithoutSaving = useCallback(() => {
    clearTimeout(compareTimer.current);
    clearRecovery(slideId);
    allowUnload.current = true;
  }, [slideId]);

  const discard = useCallback(() => {
    leaveWithoutSaving();
    window.location.reload();
  }, [leaveWithoutSaving]);

  // Closing or reloading the tab with unsaved changes asks first. The
  // recovery copy covers what cannot be blocked (browser back, crashes).
  useEffect(() => {
    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      if (allowUnload.current || status === 'saved') return;
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [status]);

  useEffect(() => () => clearTimeout(compareTimer.current), []);

  const value = useMemo(
    () => ({
      status,
      save,
      discard,
      leaveWithoutSaving,
      markLoaded,
      markChanged,
      recovery,
      resolveRecovery,
      initialProjectData,
    }),
    [
      status,
      save,
      discard,
      leaveWithoutSaving,
      markLoaded,
      markChanged,
      recovery,
      resolveRecovery,
      initialProjectData,
    ],
  );

  return <Context.Provider value={value}>{children}</Context.Provider>;
}

function useSlideSave() {
  const context = useContext(Context);
  if (!context) {
    throw new Error('useSlideSave must be used within a SaveProvider');
  }
  return context;
}

export { SaveProvider, useSlideSave };
